import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useProperty } from "@/context/PropertyContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, statusBarClass } from "@/components/app/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Loader2, Wrench, CheckCircle2, AlertTriangle, User, Plus } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/app/PhotoUploader";

export default function Tasks() {
  const { selectedId, selected } = useProperty();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [zones, setZones] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterZone, setFilterZone] = useState("all");
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [ticketForm, setTicketForm] = useState({ category: "", description: "", priority: "medium" });
  const [createOpen, setCreateOpen] = useState(false);
  const emptyForm = { name: "", description: "", zone_id: "", area_id: "", priority: "medium", due_at: "", assigned_to: "", photo_required: false, remarks_required: false };
  const [createForm, setCreateForm] = useState(emptyForm);
  const [areas, setAreas] = useState([]);

  const load = useCallback(async () => {
    if (!selectedId) return;
    const [t, s, z, a] = await Promise.all([
      api.get(`/tasks?property_id=${selectedId}`),
      api.get(`/users?property_id=${selectedId}`).catch(() => ({ data: [] })),
      api.get(`/zones?property_id=${selectedId}`).catch(() => ({ data: [] })),
      api.get(`/areas?property_id=${selectedId}`).catch(() => ({ data: [] })),
    ]);
    setTasks(t.data); setStaff(s.data); setZones(z.data); setAreas(a.data);
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/generate?property_id=${selectedId}`);
      toast.success(`Generated ${data.created} new tasks`);
      load();
    } catch (e) {
      logger.error("Task generation failed", e);
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterZone !== "all" && t.zone_id !== filterZone) return false;
      return true;
    });
  }, [tasks, filterStatus, filterZone]);

  const filteredAreas = useMemo(() => (
    areas.filter((a) => !createForm.zone_id || a.zone_id === createForm.zone_id)
  ), [areas, createForm.zone_id]);

  const assignTo = async (tid, uid) => {
    await api.put(`/tasks/${tid}/assign`, { assigned_to: uid });
    toast.success("Assigned");
    load();
  };

  const setStatus = async (tid, status) => {
    await api.put(`/tasks/${tid}`, { status });
    toast.success("Updated");
    load();
  };

  const openIssue = (t) => { setActiveTask(t); setTicketForm({ category: t.name, description: "", priority: t.priority || "medium" }); setIssueOpen(true); };

  const submitIssue = async () => {
    try {
      await api.put(`/tasks/${activeTask.id}`, { status: "issue_found", remarks: ticketForm.description });
      await api.post(`/tickets`, {
        property_id: activeTask.property_id,
        zone_id: activeTask.zone_id,
        area_id: activeTask.area_id,
        task_id: activeTask.id,
        category: ticketForm.category,
        description: ticketForm.description,
        priority: ticketForm.priority,
        photo_paths: [],
      });
      toast.success("Issue raised & ticket created");
      setIssueOpen(false);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const submitCreate = async () => {
    if (!createForm.name.trim()) { toast.error("Task name required"); return; }
    try {
      const payload = {
        property_id: selectedId,
        name: createForm.name,
        description: createForm.description,
        zone_id: createForm.zone_id || null,
        area_id: createForm.area_id || null,
        priority: createForm.priority,
        due_at: createForm.due_at ? new Date(createForm.due_at).toISOString() : null,
        assigned_to: createForm.assigned_to || null,
        photo_required: createForm.photo_required,
        remarks_required: createForm.remarks_required,
      };
      await api.post(`/tasks`, payload);
      toast.success("Task created");
      setCreateOpen(false);
      setCreateForm(emptyForm);
      load();
    } catch (e) { toast.error("Failed to create task"); }
  };

  if (!selectedId) return <div className="text-zinc-500">Select a property.</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">{selected?.name}</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Tasks</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setCreateForm(emptyForm)} variant="outline" data-testid="new-task-button">
                <Plus className="w-4 h-4 mr-2" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create New Task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Task name</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Clean Villa 3 patio" data-testid="task-create-name" />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} data-testid="task-create-description" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label>Zone</Label>
                    <Select value={createForm.zone_id} onValueChange={(v) => setCreateForm({ ...createForm, zone_id: v, area_id: "" })}>
                      <SelectTrigger data-testid="task-create-zone"><SelectValue placeholder="Zone" /></SelectTrigger>
                      <SelectContent>{zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Area (optional)</Label>
                    <Select value={createForm.area_id} onValueChange={(v) => setCreateForm({ ...createForm, area_id: v })}>
                      <SelectTrigger data-testid="task-create-area"><SelectValue placeholder="Area" /></SelectTrigger>
                      <SelectContent>
                        {filteredAreas.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={createForm.priority} onValueChange={(v) => setCreateForm({ ...createForm, priority: v })}>
                      <SelectTrigger data-testid="task-create-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high", "critical"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Due date & time</Label>
                    <Input type="datetime-local" value={createForm.due_at} onChange={(e) => setCreateForm({ ...createForm, due_at: e.target.value })} data-testid="task-create-due" />
                  </div>
                </div>
                <div>
                  <Label>Assign to</Label>
                  <Select value={createForm.assigned_to} onValueChange={(v) => setCreateForm({ ...createForm, assigned_to: v })}>
                    <SelectTrigger data-testid="task-create-assignee"><SelectValue placeholder="Select team member" /></SelectTrigger>
                    <SelectContent>
                      {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.role.replace("_", " ")}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-center justify-between p-2 border border-zinc-200 rounded">
                    <span className="text-xs">Photo required</span>
                    <Switch checked={createForm.photo_required} onCheckedChange={(v) => setCreateForm({ ...createForm, photo_required: v })} data-testid="task-create-photo-required" />
                  </div>
                  <div className="flex items-center justify-between p-2 border border-zinc-200 rounded">
                    <span className="text-xs">Remarks required</span>
                    <Switch checked={createForm.remarks_required} onCheckedChange={(v) => setCreateForm({ ...createForm, remarks_required: v })} data-testid="task-create-remarks-required" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submitCreate} className="bg-zinc-950 hover:bg-zinc-800" data-testid="task-create-submit">Create Task</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={generate} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="generate-tasks">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Today's Tasks
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "in_progress", "completed", "issue_found", "overdue"].map((s) => (
              <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterZone} onValueChange={setFilterZone}>
          <SelectTrigger className="w-[180px]" data-testid="filter-zone"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All zones</SelectItem>
            {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((t) => {
          const zone = zones.find((z) => z.id === t.zone_id);
          const assignee = staff.find((s) => s.id === t.assigned_to);
          return (
            <div key={t.id} className={`bg-white border border-zinc-200 rounded-md p-4 ${statusBarClass(t.status)}`} data-testid={`task-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="label-overline">{zone?.name || "—"}</span>
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <h4 className="font-semibold mt-1.5">{t.name}</h4>
                  {t.description && <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p>}
                </div>
                <StatusBadge status={t.status} />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                <span>Due {t.due_at ? new Date(t.due_at).toLocaleString() : "—"}</span>
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{assignee?.name || "Unassigned"}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Select value={t.assigned_to || ""} onValueChange={(v) => assignTo(t.id, v)}>
                  <SelectTrigger className="h-8 text-xs flex-1 min-w-[120px]" data-testid={`assign-${t.id}`}><SelectValue placeholder="Assign" /></SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>))}
                  </SelectContent>
                </Select>
                {t.status !== "completed" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(t.id, "completed")} data-testid={`complete-${t.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => openIssue(t)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`issue-${t.id}`}>
                  <AlertTriangle className="w-3 h-3 mr-1" /> Issue
                </Button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 py-12">No tasks. Click "Generate Today's Tasks" to create them from your checklists.</div>
        )}
      </div>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise Maintenance Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Input value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })} data-testid="ticket-category" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} data-testid="ticket-description" />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm({ ...ticketForm, priority: v })}>
                <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["low", "medium", "high", "critical"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitIssue} className="bg-zinc-950" data-testid="submit-issue">Raise Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
